import React, { useEffect, useState } from "react"
import styled from "@emotion/styled"
import * as layouts from "layouts"
import * as authzc from "authzcached"
import * as httpx from "httpx"
import * as feedback from "brandguard/api/feedback"

const Container = styled(layouts.containers.flex)`
	flex-direction: column;
	gap: 10px;
	padding: 16px;
	background: ${layouts.theme.colors.grey.lightest};
	border-radius: 8px;
`

const SectionLabel = styled.span`
	font-size: 12px;
	font-weight: 600;
	color: ${layouts.theme.colors.grey.dark50};
	text-transform: uppercase;
`

const Entry = styled(layouts.containers.flex)`
	flex-direction: column;
	gap: 6px;
	padding: 10px;
	background: ${layouts.theme.colors.white};
	border-radius: 4px;
	border-left: 3px solid ${layouts.theme.colors.blue.blue};
`

const EntryHeader = styled(layouts.containers.flex)`
	flex-direction: row;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: ${layouts.theme.colors.grey.dark50};
`

const Submitter = styled.span`
	font-weight: 600;
	color: ${layouts.theme.colors.grey.dark50};
`

const ActionBadge = styled.span`
	font-size: 10px;
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 3px;
	background: ${layouts.theme.colors.blue.blue};
	color: ${layouts.theme.colors.white};
	text-transform: uppercase;
`

const Comment = styled.div`
	font-size: 13px;
	color: ${layouts.theme.colors.grey.dark50};
	font-style: italic;
	white-space: pre-wrap;
	padding: 6px 8px;
	background: ${layouts.theme.colors.grey.lightest};
	border-radius: 4px;
`

const Empty = styled.div`
	font-size: 13px;
	color: ${layouts.theme.colors.grey.dark50};
	font-style: italic;
	padding: 8px 0;
`

interface props {
	eventType: feedback.AssetType
	eventId: string
}

function formatTimestamp(ts: string): string {
	const parsed = new Date(ts)
	if (isNaN(parsed.getTime())) return ts
	return parsed.toLocaleString()
}

export function FeedbackHistory(props: props): JSX.Element {
	const { eventType, eventId } = props
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const [submissions, setSubmissions] = useState<feedback.FeedbackHistorySubmission[]>([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => feedback.history(eventType, eventId, authzc.bearer(authzaccount)))
			.then((r) => setSubmissions(r.submissions ?? []))
			.catch((cause) => console.warn("unable to load feedback history", cause))
			.finally(() => setLoading(false))
		return p.cancel
	}, [eventType, eventId])

	if (loading) {
		return (
			<Container>
				<SectionLabel>Feedback History</SectionLabel>
				<Empty>Loading…</Empty>
			</Container>
		)
	}

	if (submissions.length === 0) {
		return (
			<Container>
				<SectionLabel>Feedback History</SectionLabel>
				<Empty>No feedback submitted for this asset yet.</Empty>
			</Container>
		)
	}

	return (
		<Container>
			<SectionLabel>Feedback History ({submissions.length})</SectionLabel>
			{submissions.map((s: feedback.FeedbackHistorySubmission) => (
				<Entry key={s.id}>
					<EntryHeader>
						<Submitter>{s.submitter_display || s.submitter_email || s.submitter_id}</Submitter>
						<ActionBadge>{s.client_action}</ActionBadge>
						{s.email_sent && <ActionBadge>Emailed</ActionBadge>}
						<span>{formatTimestamp(s.created_at)}</span>
					</EntryHeader>
					{s.comment && <Comment>{s.comment}</Comment>}
				</Entry>
			))}
		</Container>
	)
}
